import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Typography, Form, Checkbox, message } from 'antd';
import { Phone, X, Send, Play } from 'lucide-react';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;

const VoiceModalV2 = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [didNumbers, setDidNumbers] = useState([]);
  const [appNames, setAppNames] = useState([]);

  useEffect(() => {
    if (isOpen && record) {
      setLeadMobileNumbers([]);
      fetchInitialData();
      fetchLeadData();
      form.resetFields();
    }
  }, [isOpen, record, form]);

  const fetchLeadData = async () => {
    // Immediate fallback from record
    const fallbackNos = [];
    const mainNo = record.ScheduleMobileNo || record.MobileNo;
    if (mainNo) {
      fallbackNos.push({ label: mainNo, value: mainNo });
      setLeadMobileNumbers(fallbackNos);
      form.setFieldsValue({ selectedMobiles: [mainNo] });
    }

    try {
      const lead = await dialerService.getLeadById(record.Id);
      if (lead) {
        const mobileNos = [];
        if (lead.MobileNo) mobileNos.push({ label: `${lead.CountryCode || ''} ${lead.MobileNo}`, value: lead.MobileNo });
        if (lead.MobileNo1) mobileNos.push({ label: `${lead.CountryCode1 || ''} ${lead.MobileNo1}`, value: lead.MobileNo1 });
        if (lead.MobileNo2) mobileNos.push({ label: `${lead.CountryCode2 || ''} ${lead.MobileNo2}`, value: lead.MobileNo2 });

        if (mobileNos.length > 0) {
          setLeadMobileNumbers(mobileNos);
          form.setFieldsValue({
            selectedMobiles: mobileNos.map(m => m.value)
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
    }
  };

  const [leadMobileNumbers, setLeadMobileNumbers] = useState([]);

  const fetchInitialData = async () => {
    try {
      const [didRes, appRes] = await Promise.all([
        dialerService.getDIDNumbers(),
        dialerService.getAppNames()
      ]);
      if (didRes) setDidNumbers(didRes.map(d => ({ label: d.Text, value: String(d.Value || d.Code || d.Id) })));
      if (appRes) setAppNames(appRes.map(a => ({ label: a.Text, value: String(a.Value || a.Code || a.Id) })));
    } catch (error) {
      console.error('Failed to fetch voice data:', error);
    }
  };

  const handleSend = async (values) => {
    setLoading(true);
    try {
      const { userId, parentId } = getSession();
      const voiceData = {
        EnquiryLeadId: record.Id,
        EntityType: record.EntityName || 'Lead',
        DIDNo: values.didNo,
        AppName: values.appName,
        MobileNo: values.selectedMobiles[0] || '',
        MobileNo1: values.selectedMobiles[1] || '',
        MobileNo2: values.selectedMobiles[2] || '',
        IsActive: true,
        UserId: userId,
        ParentId: parentId
      };

      const res = await dialerService.sendVoice(voiceData);
      if (res.Details) {
        message.success(res.Details);
      } else {
        message.success('Voice enquiry sent successfully');
      }
      onClose();
    } catch (error) {
      console.error('Failed to send voice:', error);
      message.error('Failed to send voice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Phone size={20} className="text-orange-600" />
          <span className="text-lg font-bold text-slate-800">Voice Enquiry</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={450}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSend}
        className="py-4"
      >
        <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-[2rem] mb-6 flex flex-col items-center text-center shadow-sm">
          <div className="p-4 bg-white rounded-2xl shadow-md mb-4 group-hover:scale-110 transition-transform">
            <Phone size={32} className="text-orange-500" />
          </div>
          <Text className="text-orange-900 font-extrabold text-xl block mb-1">Trigger Voice Call</Text>
          <Text className="text-orange-700/80 text-sm font-medium">Send an automated voice enquiry to {record?.PersonName || 'this contact'}.</Text>
        </div>

        <div className="px-1">
          <Form.Item
            name="selectedMobiles"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> CHOOSE MOBILE NUMBERS</span>}
            rules={[{ required: true, message: 'Select at least one number' }]}
            className="mb-6"
          >
            <Checkbox.Group className="w-full">
              <div className="grid grid-cols-1 gap-2">
                {leadMobileNumbers.map(m => (
                  <div key={m.value} className="flex items-center p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-orange-50 hover:border-orange-200 transition-colors cursor-pointer">
                    <Checkbox value={m.value} className="text-slate-700 font-bold w-full">{m.label}</Checkbox>
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="didNo"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> DID NUMBER</span>}
            rules={[{ required: true, message: 'Select DID Number' }]}
            className="mb-6"
          >
            <Select
              placeholder="Select DID Number"
              options={didNumbers}
              className="h-12 premium-select"
              getPopupContainer={() => document.body}
              virtual={false}
            />
          </Form.Item>

          <Form.Item
            name="appName"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> FLOW / APP NAME</span>}
            rules={[{ required: true, message: 'Select Flow' }]}
            className="mb-8"
          >
            <Select
              placeholder="Select Flow"
              options={appNames}
              className="h-12 premium-select"
              getPopupContainer={() => document.body}
              virtual={false}
            />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <Button onClick={onClose} className="rounded-xl font-bold h-12 px-8 border-slate-200 hover:border-orange-400 hover:text-orange-600 transition-all">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<Play size={18} fill="currentColor" />}
            className="rounded-xl font-black h-12 px-12 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-lg shadow-orange-200 border-none flex items-center gap-2 group"
          >
            <span>Start Voice</span>
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default VoiceModalV2;
